import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Switch,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Stack,
  Box,
  useMediaQuery,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  ButtonGroup,
  Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import axiosInstance from '../../store/api';
import { addDays, getPhotosUploadStatus } from './retailPhotosAccess';
import RetailPhotosStorageBar from './RetailPhotosStorageBar';

function getAppStatus(access) {
  if (!access?.enabled) return { label: 'Inactivo', color: 'default' };
  if (!access.expiresAt) return { label: 'Sin fecha', color: 'warning' };
  const diff = Math.ceil((new Date(access.expiresAt) - new Date()) / 86400000);
  if (diff > 7) return { label: `${diff}d restantes`, color: 'success' };
  if (diff > 0) return { label: `${diff}d (por vencer)`, color: 'warning' };
  if (diff > -7) return { label: 'Gracia', color: 'error' };
  return { label: 'Expirado', color: 'error' };
}

const PROMO_OPTIONS = [
  { label: '15d', days: 15 },
  { label: '30d', days: 30 },
  { label: '60d', days: 60 },
  { label: '90d', days: 90 },
  { label: '1 año', days: 365 },
];

const RETAIL_POS_KEY = 'retail_pos_v1';
const DEFAULT_STORAGE_MB = 500;

const normalizeUserAccess = (appsAccess = {}) => {
  const next = { ...appsAccess };
  const prev = next[RETAIL_POS_KEY] || {};
  next[RETAIL_POS_KEY] = {
    enabled: false,
    expiresAt: null,
    imagenesUpload: false,
    imagenesUploadExpiresAt: null,
    storageLimitMb: DEFAULT_STORAGE_MB,
    ...prev,
    storageLimitMb:
      Number(prev.storageLimitMb) > 0
        ? Number(prev.storageLimitMb)
        : DEFAULT_STORAGE_MB,
  };
  return next;
};

export default function UserAccessModal({ open, user, catalog, onClose, onSaved }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [localAccess, setLocalAccess] = useState(() => normalizeUserAccess(user?.appsAccess));
  const [storageUsage, setStorageUsage] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      setLocalAccess(normalizeUserAccess(user.appsAccess));
      setError('');
      setSuccess('');
      setStorageUsage(null);
      setStorageLoading(true);
      axiosInstance
        .get(`/users/${user._id}/retail-storage-usage`)
        .then((res) => setStorageUsage(res.data))
        .catch(() => setStorageUsage({ totalBytes: 0, fileCount: 0, storageLimitMb: DEFAULT_STORAGE_MB }))
        .finally(() => setStorageLoading(false));
    }
  }, [open, user]);

  const handleToggle = (key, enabled) => {
    setLocalAccess(prev => ({
      ...prev,
      [key]: { ...prev[key], enabled },
    }));
  };

  const handleDate = (key, value) => {
    setLocalAccess(prev => ({
      ...prev,
      [key]: { ...prev[key], expiresAt: value || null },
    }));
  };

  const handleRetailPremium = (field, value) => {
    setLocalAccess(prev => ({
      ...prev,
      [RETAIL_POS_KEY]: { ...prev[RETAIL_POS_KEY], [field]: value },
    }));
  };

  const applyPromo = (key, days) => {
    setLocalAccess(prev => ({
      ...prev,
      [key]: { ...prev[key], enabled: true, expiresAt: addDays(days) },
    }));
    setSuccess(`Promo ${days}d aplicada a ${catalog[key]?.title || key}`);
    setTimeout(() => setSuccess(''), 2500);
  };

  const applyPhotosPromo = (days) => {
    setLocalAccess(prev => ({
      ...prev,
      [RETAIL_POS_KEY]: {
        ...prev[RETAIL_POS_KEY],
        imagenesUpload: true,
        imagenesUploadExpiresAt: addDays(days),
      },
    }));
    setSuccess(`Fotos S3: promo ${days}d aplicada`);
    setTimeout(() => setSuccess(''), 2500);
  };

  const applyPromoAll = (days, onlyPremium = false) => {
    setLocalAccess(prev => {
      const next = { ...prev };
      Object.entries(catalog).forEach(([key, app]) => {
        if (onlyPremium && !app.premium) return;
        next[key] = { ...next[key], enabled: true, expiresAt: addDays(days) };
      });
      return next;
    });
    setSuccess(`Promo ${days}d aplicada a ${onlyPremium ? 'apps premium' : 'todas las apps'}`);
    setTimeout(() => setSuccess(''), 2500);
  };

  const revokeAll = () => {
    setLocalAccess(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        if (key === RETAIL_POS_KEY) {
          next[key] = {
            ...next[key],
            enabled: false,
            expiresAt: null,
            imagenesUpload: false,
            imagenesUploadExpiresAt: null,
          };
        } else {
          next[key] = { ...next[key], enabled: false, expiresAt: null };
        }
      });
      return next;
    });
  };

  const save = async () => {
    setError('');
    for (const [, access] of Object.entries(localAccess)) {
      if (access?.enabled && !access?.expiresAt) {
        setError('No puedes activar una app sin fecha de expiración.');
        return;
      }
    }
    const retail = localAccess[RETAIL_POS_KEY];
    if (retail?.imagenesUpload && !retail?.imagenesUploadExpiresAt) {
      setError('Las fotos de producto requieren fecha de expiración.');
      return;
    }
    setSaving(true);
    try {
      const res = await axiosInstance.patch(`/users/${user._id}/apps-access`, { appsAccess: localAccess });
      onSaved(res.data);
    } catch (e) {
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const premiumApps = Object.entries(catalog).filter(([, a]) => a.premium);
  const freeApps = Object.entries(catalog).filter(([, a]) => !a.premium);

  const renderAppRow = ([key, app]) => {
    const access = localAccess[key] || { enabled: false, expiresAt: '' };
    const status = getAppStatus(access);
    const isRetailPos = key === RETAIL_POS_KEY;

    return (
      <Card key={key} variant="outlined" sx={{ borderLeft: access.enabled ? '3px solid #4caf50' : '3px solid transparent' }}>
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Box sx={{ minWidth: 140 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2" fontWeight={700}>{app.title}</Typography>
                {app.premium && <Chip label={`${app.price} Bs`} size="small" color="warning" sx={{ height: 20, fontSize: 11 }} />}
              </Stack>
              <Chip label={status.label} size="small" color={status.color} sx={{ mt: 0.5, height: 20, fontSize: 10 }} />
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <ButtonGroup size="small" variant="outlined">
                {PROMO_OPTIONS.map(p => (
                  <Button key={p.days} onClick={() => applyPromo(key, p.days)} sx={{ fontSize: 11, px: 1, minWidth: 0 }}>
                    {p.label}
                  </Button>
                ))}
              </ButtonGroup>
              <Switch checked={!!access.enabled} onChange={e => handleToggle(key, e.target.checked)} size="small" />
              <TextField
                type="date"
                size="small"
                value={access.expiresAt?.substring(0, 10) || ''}
                error={access.enabled && !access.expiresAt}
                onChange={e => handleDate(key, e.target.value)}
                sx={{ width: 145 }}
                InputProps={{ sx: { fontSize: 13 } }}
              />
            </Stack>
          </Stack>

          {key === 'corebase_crm_v1' && <TextField
            label="Vendedores extra (USD 1/mes o USD 10/año cada uno)" type="number" size="small"
            value={access.additionalSellerSeats ?? 0} inputProps={{ min: 0, max: 1000, step: 1 }}
            onChange={e => setLocalAccess(prev => ({ ...prev, [key]: { ...prev[key], additionalSellerSeats: Number(e.target.value) } }))}
            sx={{ mt: 1.5, width: '100%' }}
          />}
          {isRetailPos && (() => {
            const photosStatus = getPhotosUploadStatus(access);
            return (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                    Premium — fotos de producto (S3)
                  </Typography>
                  <Chip label={photosStatus.label} size="small" color={photosStatus.color} sx={{ fontWeight: 600 }} />
                </Stack>

                <ButtonGroup size="small" variant="outlined" sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                  {PROMO_OPTIONS.map(p => (
                    <Button
                      key={`photos-${p.days}`}
                      onClick={() => applyPhotosPromo(p.days)}
                      sx={{ fontSize: 11, px: 1, minWidth: 0 }}
                    >
                      {p.label}
                    </Button>
                  ))}
                </ButtonGroup>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Switch
                      checked={!!access.imagenesUpload}
                      onChange={(e) => handleRetailPremium('imagenesUpload', e.target.checked)}
                      size="small"
                    />
                    <Typography variant="body2">Permitir subir fotos</Typography>
                  </Stack>
                  <TextField
                    type="date"
                    size="small"
                    label="Vence fotos"
                    value={access.imagenesUploadExpiresAt?.substring(0, 10) || ''}
                    error={!!access.imagenesUpload && !access.imagenesUploadExpiresAt}
                    disabled={!access.imagenesUpload}
                    onChange={(e) => handleRetailPremium('imagenesUploadExpiresAt', e.target.value || null)}
                    sx={{ width: { xs: '100%', sm: 155 } }}
                    InputProps={{ sx: { fontSize: 13 } }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Límite almacenamiento (MB)"
                    type="number"
                    size="small"
                    value={access.storageLimitMb ?? DEFAULT_STORAGE_MB}
                    onChange={(e) => {
                      const mb = parseInt(e.target.value, 10);
                      handleRetailPremium(
                        'storageLimitMb',
                        Number.isFinite(mb) && mb > 0 ? mb : DEFAULT_STORAGE_MB
                      );
                    }}
                    disabled={!access.imagenesUpload}
                    inputProps={{ min: 1, max: 10240, step: 50 }}
                    helperText="500 MB por defecto · máx. 10 GB"
                    sx={{ width: { xs: '100%', sm: 200 } }}
                  />
                </Stack>

                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.75}>
                    Uso actual en S3
                  </Typography>
                  <RetailPhotosStorageBar
                    totalBytes={storageUsage?.totalBytes ?? 0}
                    fileCount={storageUsage?.fileCount ?? 0}
                    storageLimitMb={
                      access.storageLimitMb ?? storageUsage?.storageLimitMb ?? DEFAULT_STORAGE_MB
                    }
                    loading={storageLoading}
                  />
                </Box>
              </Box>
            );
          })()}
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: '#fff', py: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>{user.nombre} {user.apellido}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>{user.email} · {user.personal_code}</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 1.5, sm: 3 }, py: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Acciones rápidas masivas */}
        <Card variant="outlined" sx={{ mb: 2, bgcolor: '#f5f0ff' }}>
          <CardContent sx={{ py: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <CardGiftcardIcon sx={{ color: '#6366f1' }} />
              <Typography variant="subtitle2" fontWeight={700}>Promociones rápidas</Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {PROMO_OPTIONS.map(p => (
                <Button key={p.days} size="small" variant="contained" onClick={() => applyPromoAll(p.days, true)}
                  sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, textTransform: 'none', fontSize: 12 }}>
                  Premium {p.label}
                </Button>
              ))}
              <Divider orientation="vertical" flexItem />
              <Button size="small" variant="outlined" startIcon={<CheckCircleIcon />} onClick={() => applyPromoAll(90, false)}
                sx={{ textTransform: 'none', fontSize: 12 }}>
                Todo 90d
              </Button>
              <Button size="small" variant="outlined" color="error" startIcon={<BlockIcon />} onClick={revokeAll}
                sx={{ textTransform: 'none', fontSize: 12 }}>
                Revocar todo
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Apps Premium */}
        <Typography variant="overline" fontWeight={700} color="warning.main" sx={{ mb: 1, display: 'block' }}>
          Apps Premium ({premiumApps.length})
        </Typography>
        <Stack spacing={1} mb={2}>
          {premiumApps.map(renderAppRow)}
        </Stack>

        {/* Apps Gratuitas */}
        <Typography variant="overline" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Apps Gratuitas ({freeApps.length})
        </Typography>
        <Stack spacing={1}>
          {freeApps.map(renderAppRow)}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
